const DEFAULT_PING_INTERVAL = 30000;
const MAX_RECONNECT_DELAY = 30000;

export function buildChatSocketUrl(
    configuredBaseUrl,
    socketId,
    token
) {
    if (!configuredBaseUrl || !socketId || !token) {
        return '';
    }

    let baseUrl = configuredBaseUrl;

    if (/^https?:\/\//i.test(baseUrl)) {
        baseUrl = baseUrl.replace(/^http/i, 'ws');
    } else if (!/^wss?:\/\//i.test(baseUrl)) {
        baseUrl = `wss://${baseUrl}`;
    }

    return (
        `${baseUrl.replace(/\/+$/, '')}` +
        `/ws/chat/${socketId}/` +
        `?token=${encodeURIComponent(token)}`
    );
}

export default class ChatSocketClient {
    constructor(options) {
        const config = options || {};

        this.createUrl =
            typeof config.createUrl === 'function'
                ? config.createUrl
                : () => '';

        this.onMessage =
            typeof config.onMessage === 'function'
                ? config.onMessage
                : () => {};

        this.onStateChange =
            typeof config.onStateChange === 'function'
                ? config.onStateChange
                : () => {};

        this.onError =
            typeof config.onError === 'function'
                ? config.onError
                : () => {};

        this.isActive =
            typeof config.isActive === 'function'
                ? config.isActive
                : () => true;

        this.WebSocketClass =
            config.WebSocketClass ||
            (
                typeof WebSocket !== 'undefined'
                    ? WebSocket
                    : null
            );

        this.pingIntervalMs =
            config.pingIntervalMs ||
            DEFAULT_PING_INTERVAL;

        this.socket = null;
        this.socketId = '';
        this.connected = false;
        this.shouldReconnect = false;
        this.reconnectAttempts = 0;
        this.reconnectTimer = null;
        this.pingTimer = null;
        this.destroyed = false;
    }

    connect(socketId) {
        if (
            this.destroyed ||
            !this.isActive() ||
            !socketId ||
            !this.WebSocketClass
        ) {
            return false;
        }

        const normalizedSocketId = String(socketId);

        if (
            this.socket &&
            this.socketId === normalizedSocketId &&
            (
                this.socket.readyState ===
                    this.WebSocketClass.OPEN ||
                this.socket.readyState ===
                    this.WebSocketClass.CONNECTING
            )
        ) {
            return true;
        }

        this.close('Switching chat room');

        this.destroyed = false;
        this.shouldReconnect = true;
        this.socketId = normalizedSocketId;
        this.reconnectAttempts = 0;
        this.emitState();

        return this.openSocket(normalizedSocketId);
    }

    openSocket(socketId) {
        if (
            this.destroyed ||
            !this.shouldReconnect ||
            !this.isActive()
        ) {
            return false;
        }

        const socketUrl = this.createUrl(socketId);

        if (!socketUrl) {
            this.onError(
                new Error(
                    'WebSocket URL or authentication token is missing.'
                )
            );
            return false;
        }

        this.clearReconnectTimer();

        let socket = null;

        try {
            socket = new this.WebSocketClass(
                socketUrl
            );
        } catch (error) {
            this.onError(error);
            return false;
        }

        this.socket = socket;
        this.connected = false;
        this.emitState();

        socket.onopen = () => {
            if (this.socket !== socket) {
                this.detachAndCloseSocket(
                    socket,
                    'Stale connection'
                );
                return;
            }

            this.connected = true;
            this.reconnectAttempts = 0;
            this.startPing(socket);
            this.emitState();
        };

        socket.onmessage = (event) => {
            if (this.socket === socket) {
                this.onMessage(event);
            }
        };

        socket.onclose = () => {
            if (this.socket !== socket) {
                return;
            }

            this.socket = null;
            this.connected = false;
            this.clearPing();
            this.emitState();

            if (
                this.shouldReconnect &&
                !this.destroyed &&
                this.isActive() &&
                this.socketId === socketId
            ) {
                this.scheduleReconnect(socketId);
            }
        };

        socket.onerror = (error) => {
            if (this.socket === socket) {
                this.onError(error);
            }
        };

        return true;
    }

    send(payload) {
        if (!this.isOpen()) {
            return false;
        }

        try {
            const data =
                typeof payload === 'string'
                    ? payload
                    : JSON.stringify(payload);

            this.socket.send(data);
            return true;
        } catch (error) {
            this.onError(error);
            return false;
        }
    }

    isOpen() {
        return Boolean(
            this.socket &&
            this.WebSocketClass &&
            this.socket.readyState ===
                this.WebSocketClass.OPEN
        );
    }

    startPing(socket) {
        this.clearPing();

        this.pingTimer = setInterval(() => {
            if (
                this.socket === socket &&
                this.isOpen()
            ) {
                this.send({
                    type: 'ping'
                });
            }
        }, this.pingIntervalMs);
    }

    clearPing() {
        if (this.pingTimer) {
            clearInterval(this.pingTimer);
            this.pingTimer = null;
        }
    }

    scheduleReconnect(socketId) {
        if (
            this.reconnectTimer ||
            this.destroyed ||
            !this.shouldReconnect ||
            !this.isActive()
        ) {
            return;
        }

        const baseDelay = Math.min(
            1000 * Math.pow(2, this.reconnectAttempts),
            MAX_RECONNECT_DELAY
        );

        const reconnectDelay =
            baseDelay +
            Math.floor(Math.random() * 250);

        this.reconnectAttempts += 1;

        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;

            if (
                this.shouldReconnect &&
                !this.destroyed &&
                this.isActive() &&
                this.socketId === socketId
            ) {
                this.openSocket(socketId);
            }
        }, reconnectDelay);
    }

    clearReconnectTimer() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }

    close(reason) {
        this.shouldReconnect = false;
        this.connected = false;
        this.clearReconnectTimer();
        this.clearPing();

        const socket = this.socket;

        this.socket = null;
        this.socketId = '';

        if (socket) {
            this.detachAndCloseSocket(
                socket,
                reason || 'Chat room closed'
            );
        }

        this.emitState();
    }

    detachAndCloseSocket(socket, reason) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onclose = null;
        socket.onerror = null;

        if (
            socket.readyState ===
                this.WebSocketClass.OPEN ||
            socket.readyState ===
                this.WebSocketClass.CONNECTING
        ) {
            try {
                socket.close(1000, reason);
            } catch (error) {
                this.onError(error);
            }
        }
    }

    destroy() {
        this.destroyed = true;
        this.close('Component destroyed');
        this.destroyed = true;
    }

    emitState() {
        this.onStateChange({
            socket: this.socket,
            socketId: this.socketId,
            connected: this.connected
        });
    }
}
